/**
 * What a Sync actually did, shown after the run finishes.
 *
 * Partial success is the sync's normal outcome — one card can fail to create while the rest
 * succeed, and a repo without sub-issue support silently gets body references instead. Both are
 * states the user has to be able to see, so this reports the counts, the individual failures, and
 * any capability fallbacks rather than collapsing everything into a toast.
 *
 * Reuses the shared Modal shell and follows AddToTreeModal's class-naming shape.
 */
import React from 'react';
import Modal from 'components/ui/Modal';
import { SyncReport } from 'api/sync';

interface Props {
    /** Null when the run failed before it could produce a report — `error` carries why. */
    report: SyncReport | null;
    error: string | null;
    onClose: () => void;
}

interface Stat {
    label: string;
    value: number;
}

/** Only counts worth reading are shown — a row of zeroes tells the user nothing. */
function visibleStats(stats: Stat[]): Stat[] {
    const shown = stats.filter((stat) => stat.value > 0);
    return shown.length > 0 ? shown : [stats[0]];
}

export default function SyncResultModal({ report, error, onClose }: Props) {
    if (error) {
        return (
            <Modal title='Sync failed' onClose={onClose} className='sync-result-modal'>
                <p className='sync-result-modal__error'>{error}</p>
                <div className='sync-result-modal__footer'>
                    <button className='sync-result-modal__btn' onClick={onClose}>Close</button>
                </div>
            </Modal>
        );
    }

    if (!report) return null;

    const { items, relations, warnings, repoFullName } = report;
    const failures = [...items.failed, ...relations.failed];
    const nothingHappened = items.created === 0 && items.updated === 0
        && relations.applied === 0 && relations.removed === 0 && failures.length === 0;

    return (
        <Modal
            title={failures.length > 0 ? 'Synced with problems' : 'Sync complete'}
            onClose={onClose}
            className='sync-result-modal'
        >
            <p className='sync-result-modal__description'>
                {nothingHappened
                    ? <>Everything was already up to date in <strong>{repoFullName}</strong>.</>
                    : <>Pushed to <strong>{repoFullName}</strong>.</>}
            </p>

            <div className='sync-result-modal__section'>
                <h4 className='sync-result-modal__section-title'>Cards</h4>
                <ul className='sync-result-modal__stats'>
                    {visibleStats([
                        { label: 'issues created', value: items.created },
                        { label: 'issues updated', value: items.updated },
                        { label: 'links verified', value: items.verified },
                        { label: 're-created after being deleted on GitHub', value: items.relinked },
                        { label: 'added to the project board', value: items.boarded },
                        { label: 'placements with no card left', value: items.stale },
                    ]).map((stat) => (
                        <li key={stat.label} className='sync-result-modal__stat'>
                            <span className='sync-result-modal__stat-value'>{stat.value}</span> {stat.label}
                        </li>
                    ))}
                </ul>
            </div>

            <div className='sync-result-modal__section'>
                <h4 className='sync-result-modal__section-title'>Relationships</h4>
                <ul className='sync-result-modal__stats'>
                    {visibleStats([
                        { label: 'links applied', value: relations.applied },
                        { label: 'links removed', value: relations.removed },
                        { label: 'already correct', value: relations.unchanged },
                        { label: 'skipped (no type, or an end that is not synced)', value: relations.skipped },
                    ]).map((stat) => (
                        <li key={stat.label} className='sync-result-modal__stat'>
                            <span className='sync-result-modal__stat-value'>{stat.value}</span> {stat.label}
                        </li>
                    ))}
                </ul>
            </div>

            {warnings.length > 0 && (
                <div className='sync-result-modal__section'>
                    <h4 className='sync-result-modal__section-title'>Notes</h4>
                    <ul className='sync-result-modal__notes'>
                        {warnings.map((warning) => (
                            <li key={warning} className='sync-result-modal__note'>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}

            {failures.length > 0 && (
                <div className='sync-result-modal__section'>
                    <h4 className='sync-result-modal__section-title'>
                        Could not sync ({failures.length})
                    </h4>
                    <ul className='sync-result-modal__failures'>
                        {items.failed.map((failure) => (
                            <li key={failure.itemId} className='sync-result-modal__failure'>
                                <strong>{failure.title || failure.itemId}</strong> — {failure.error}
                            </li>
                        ))}
                        {relations.failed.map((failure, index) => (
                            <li key={failure.connectionId || `relation-${index}`} className='sync-result-modal__failure'>
                                <strong>A connection</strong> — {failure.error}
                            </li>
                        ))}
                    </ul>
                    <p className='sync-result-modal__retry-hint'>
                        Everything else was synced. Running Sync again will retry only what failed.
                    </p>
                </div>
            )}

            <div className='sync-result-modal__footer'>
                <button className='sync-result-modal__btn' onClick={onClose}>Close</button>
            </div>
        </Modal>
    );
}
